import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { TEMPLATES } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import RichEditor from "@/components/RichEditor";
import { Plus, Trash2, Star } from "lucide-react";
import { toast } from "sonner";

const TEMPLATE_INSERT_MENU = [
  { label: "{{first_name}}", value: "{{first_name}}", title: "Guest first name" },
  { label: "{{property}}", value: "{{property}}", title: "Property name" },
  { label: "{{check_in}}", value: "{{check_in}}", title: "Check-in date" },
  { label: "{{check_out}}", value: "{{check_out}}", title: "Check-out date" },
  { label: "if:family", value: "{{if:family}}…{{/if}}", title: "Show only for family guests", testid: "if-family" },
  { label: "if:dog-owner", value: "{{if:dog-owner}}…{{/if}}", title: "Show only for dog owners", testid: "if-dog-owner" },
  { label: "if:corporate", value: "{{if:corporate}}…{{/if}}", title: "Show only for corporate guests", testid: "if-corporate" },
  { label: "if:not:dog-owner", value: "{{if:not:dog-owner}}…{{/if}}", title: "Show only for non-dog-owners", testid: "if-not-dog-owner" },
];

export default function Templates() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ name: "", subject: "", html_content: "" });

  const load = () => api.get("/templates").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/templates", form);
      toast.success("Template saved");
      setOpen(false);
      setForm({ name: "", subject: "", html_content: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/templates/${id}`);
    load();
  };

  const setAsThankyou = async (t) => {
    try {
      await api.post(`/templates/${t.id}/set-thankyou`);
      toast.success(`"${t.name}" is now the post-departure default`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const testSend = async (t) => {
    const to = window.prompt("Send test to which email?", "");
    if (!to) return;
    try {
      await api.post("/campaigns/test", {
        subject: t.subject,
        html_content: t.html_content,
        test_email: to,
      });
      toast.success(`Test sent to ${to}`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Layout
      title="Email templates"
      subtitle="Reusable HTML templates for post-departure thank-yous, seasonal invitations and contractor briefings."
      actions={
        <Button
          data-testid={TEMPLATES.addBtn}
          onClick={() => setOpen(true)}
          className="rounded-full ns-btn-primary h-11 px-6"
        >
          <Plus className="w-4 h-4 mr-2" /> New template
        </Button>
      }
    >
      <div data-testid={TEMPLATES.root} className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        {rows.length === 0 && (
          <div className="ns-card p-10 text-center col-span-full">
            <div className="font-serif text-2xl">No templates yet</div>
            <p className="text-sm text-slate-500 mt-2">
              Save a reusable HTML block once, drop it into every campaign.
            </p>
          </div>
        )}
        {rows.map((t) => (
          <div key={t.id} className="ns-card p-6 flex flex-col">
            <div className="flex items-start justify-between gap-2">
              <div className="ns-label">Template</div>
              {t.is_thankyou_default && (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-100">
                  <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Post-departure default
                </span>
              )}
            </div>
            <div className="font-serif text-xl mt-1">{t.name}</div>
            <div className="text-sm text-slate-500 mt-1 line-clamp-1">{t.subject}</div>
            <div className="mt-4 email-preview text-xs" style={{ maxHeight: 140, overflow: "hidden" }}
                 dangerouslySetInnerHTML={{ __html: t.html_content }} />
            <div className="mt-4 flex items-center justify-between">
              <button
                onClick={() => setPreview(t)}
                className="text-xs text-teal-700 hover:underline"
              >
                Preview
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => testSend(t)}
                  className="text-[11px] text-slate-500 hover:text-teal-700"
                  title="Send this template to a test address"
                >
                  Send test
                </button>
                {!t.is_thankyou_default && (
                  <button
                    onClick={() => setAsThankyou(t)}
                    className="text-[11px] text-slate-500 hover:text-amber-700 inline-flex items-center gap-1"
                    title="Use for auto thank-you after checkout"
                  >
                    <Star className="w-3 h-3" /> Set as thank-you
                  </button>
                )}
                <button
                  onClick={() => del(t.id)}
                  className="text-slate-300 hover:text-red-600 p-1.5"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">New template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="ns-label">Name</Label>
              <Input
                data-testid={TEMPLATES.nameInput}
                className="mt-2 h-11 bg-white"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label className="ns-label">Subject</Label>
              <Input
                data-testid={TEMPLATES.subjectInput}
                className="mt-2 h-11 bg-white"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
              />
            </div>
            <div>
              <Label className="ns-label">Body</Label>
              <div className="mt-2">
                <RichEditor
                  testId={TEMPLATES.htmlInput}
                  value={form.html_content}
                  onChange={(html) => setForm({ ...form, html_content: html })}
                  placeholder="Compose your reusable email…"
                  insertMenu={TEMPLATE_INSERT_MENU}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              data-testid={TEMPLATES.saveBtn}
              onClick={save}
              className="rounded-full ns-btn-primary px-6"
            >
              Save template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{preview.name}</DialogTitle>
              </DialogHeader>
              <div className="ns-label">Subject</div>
              <div className="mb-4">{preview.subject}</div>
              <div className="email-preview" dangerouslySetInnerHTML={{ __html: preview.html_content }} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
